/**
 * «Обращения» (`/requests`, `GET /api/tg/requests`): всё, что сотрудники написали в личку
 * бота @unirenteraibot. Закрыто правом `requests_view`. Спека `docs/specs/2026-09-25-tg-requests-design.md`.
 */
import { useQuery } from "@tanstack/react-query";

import { NoAccess } from "@/components/NoAccess";
import { REQUESTS_VIEW, useCan } from "@/lib/access";
import { apiFetch } from "@/lib/api";

type Message = { date: string; kind: string; text: string };
type Request = {
  id: number;
  crm_user_id: number | null;
  name: string;
  date_first: string;
  date_last: string;
  status: string;
  before: { date: string; text: string } | null;
  messages: Message[];
};
type Answer = { requests: Request[]; unregistered: number };

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
/** «2026-09-25T16:08:03» → «25.09 16:08» (время базы — уже московское). */
const when = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`;

export function RequestsPage() {
  return useCan(REQUESTS_VIEW) ? <Screen /> : <NoAccess what="обращениям" />;
}

function Screen() {
  const q = useQuery({ queryKey: ["tg-requests"], queryFn: () => apiFetch<Answer>("/api/tg/requests") });
  return (
    <main className="min-h-0 flex-1 overflow-y-auto p-6">
      {q.isPending && <p className="text-fg-muted">Загрузка…</p>}
      {q.isError && <p className="text-err">Не удалось загрузить обращения</p>}
      {q.data && (
        <div className="flex max-w-[920px] flex-col gap-2">
          <p className="text-[12px] text-fg-muted">
            Обращений: {q.data.requests.length} · авторов: {new Set(q.data.requests.map((r) => r.name)).size}
            {q.data.unregistered > 0 && ` · сообщений без заявки: ${q.data.unregistered}`}
          </p>
          {q.data.requests.length === 0 && <p className="text-fg-muted">Обращений пока нет</p>}
          {q.data.requests.map((r) => (
            <section key={r.id} className={`${card} px-4 py-3`}>
              <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="tabular-nums text-fg-muted">№{r.id}</span>
                <span className="font-medium">{r.name || "Без имени"}</span>
                {!r.crm_user_id && <span className="text-[12px] text-warn">нет в CRM</span>}
                <span className="text-[12px] tabular-nums text-fg-muted">{when(r.date_first)}</span>
              </header>
              {r.messages.map((m, i) => (
                <p key={i} className="mt-2 whitespace-pre-wrap text-[14px] leading-[1.5]">
                  {r.messages.length > 1 && (
                    <span className="mr-2 text-[12px] tabular-nums text-fg-muted">{m.date.slice(11, 16)}</span>
                  )}
                  {m.text || <span className="text-fg-muted">‹{m.kind} без текста›</span>}
                </p>
              ))}
              {r.before && (
                <details className="mt-2 text-[12px] text-fg-muted">
                  <summary className="cursor-pointer">Перед этим бот писал · {when(r.before.date)}</summary>
                  <p className="mt-1 whitespace-pre-wrap border-l-2 border-border pl-3">{r.before.text}</p>
                </details>
              )}
            </section>
          ))}
        </div>
      )}
    </main>
  );
}
